import React, { useEffect, useState } from "react";
import { toast } from "./ui/sonner";
import { Button } from "./ui/button";
import { useApp } from "../context/AppContext";
import { Mail, Loader2, Send, FlaskConical, CheckCircle2, XCircle, Archive, Eye, X, Paperclip } from "lucide-react";
import { fmtDT } from "../lib/date";

const money = (n) => "$" + Math.round(Number(n || 0)).toLocaleString();

export const VoucherMailCard = ({ zh }) => {
  const { api, user } = useApp();
  const [d, setD] = useState(null);
  const [emails, setEmails] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [period, setPeriod] = useState("");
  const [dry, setDry] = useState(null);
  const [preview, setPreview] = useState(null);
  const allowed = ["admin", "finance", "ceo"].includes(user?.role);

  const load = () => api.get("/finance/payment-record/voucher-mail").then(({ data }) => { setD(data); setEmails((data.emails || []).join(", ")); setEnabled(!!data.enabled); setPeriod((p) => p || data.nextPeriod); }).catch(() => {});
  useEffect(() => { if (allowed) load(); }, [allowed]); // eslint-disable-line
  if (!allowed || !d) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/finance/payment-record/voucher-mail", { enabled, emails });
      toast.success(zh ? `已保存：${data.enabled ? "每月 1 日自动发送" : "自动发送关闭"} · ${data.emails.length} 个邮箱` : "Saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const run = async (dryRun, openPreview = false) => {
    if (!dryRun && !window.confirm(zh ? `立即把 ${period} 的全公司已付批次凭证包发送给：${(d.emails || []).join(", ") || "（未配置邮箱）"}？` : `Send ${period} package now?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/finance/payment-record/voucher-mail/run", { period, dryRun, force: !dryRun });
      if (dryRun) {
        setDry(data);
        if (openPreview) setPreview(data);
        else toast.success(zh ? `预演完成：${data.count} 笔 ${money(data.total)}，ZIP ${(data.zipBytes / 1024 / 1024).toFixed(2)} MB（未发送）` : "Dry run done");
      }
      else if (data.skipped) toast.error(zh ? (data.skipped === "no_recipients" ? "未配置会计邮箱" : "自动发送已关闭") : data.skipped);
      else { toast.success(zh ? `已发送 ${data.sentOk}/${data.recipients.length} 封 · ${data.count} 笔 ${money(data.total)}` : "Sent"); setDry(null); load(); }
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="voucher-mail-card">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Archive className="h-5 w-5 text-brand-500" />
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">{zh ? "凭证包月寄会计" : "Monthly voucher package to accountant"}</h3>
          <p className="text-[11px] text-slate-400">{zh ? "每月 1 日自动把上月全公司已付分包批次的凭证 ZIP（按项目/阶段分目录）+ 对账摘要 + 汇总 Excel 邮寄给外部会计" : "Auto-email last month's paid-batch vouchers ZIP + summary on the 1st"}</p>
        </div>
        <span data-testid="voucher-mail-status" className={`ml-auto text-[10px] font-bold px-2 py-1 rounded-full ${d.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{d.enabled ? (zh ? "自动发送 · 开" : "Auto ON") : (zh ? "自动发送 · 关" : "Auto OFF")}</span>
        {!d.emailConfigured && <span data-testid="voucher-mail-noemail" className="text-[10px] font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-700">{zh ? "邮件服务未配置（RESEND/SENDGRID）" : "Email service not configured"}</span>}
      </div>
      <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
        <label className="text-xs text-slate-600 block">{zh ? "会计邮箱（多个用逗号分隔）" : "Accountant emails (comma separated)"}
          <input data-testid="voucher-mail-emails" value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="accountant@cpa.com, bookkeeper@firm.com" className="mt-1 w-full border border-slate-200 rounded-md px-2.5 py-2 text-sm" />
        </label>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid="voucher-mail-enabled-row"><input type="checkbox" data-testid="voucher-mail-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 accent-brand-500" />{zh ? "每月 1 日自动发送" : "Auto on the 1st"}</label>
          <Button size="sm" disabled={busy} onClick={save} data-testid="voucher-mail-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (zh ? "保存" : "Save")}</Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
        <label className="text-xs text-slate-600 flex items-center gap-1.5">{zh ? "期间" : "Period"}<input type="month" data-testid="voucher-mail-period" value={period} onChange={(e) => setPeriod(e.target.value)} className="border border-slate-200 rounded-md px-2 py-1 text-xs" /></label>
        <Button variant="outline" size="sm" disabled={busy} onClick={() => run(true)} data-testid="voucher-mail-dryrun"><FlaskConical className="h-4 w-4 mr-1" />{zh ? "预演（不发送）" : "Dry run"}</Button>
        <Button variant="outline" size="sm" disabled={busy} onClick={() => run(true, true)} data-testid="voucher-mail-preview"><Eye className="h-4 w-4 mr-1" />{zh ? "预览邮件" : "Preview email"}</Button>
        <Button size="sm" disabled={busy || !(d.emails || []).length} onClick={() => run(false)} data-testid="voucher-mail-send" className="bg-brand-500 hover:bg-brand-600 text-white"><Send className="h-4 w-4 mr-1" />{zh ? "立即发送该月" : "Send now"}</Button>
        {dry && (
          <span className="text-xs text-slate-600 inline-flex items-center gap-2" data-testid="voucher-mail-dry-result">
            <Mail className="h-3.5 w-3.5 text-slate-400" />{zh ? `${dry.period}：${dry.projects.length} 个项目 · ${dry.count} 笔 · ${money(dry.total)} · ZIP ${(dry.zipBytes / 1024 / 1024).toFixed(2)} MB` : `${dry.period}: ${dry.count} batches ${money(dry.total)}`}
          </span>
        )}
      </div>
      {dry?.projects?.length > 0 && (
        <div className="mt-2 max-h-40 overflow-auto rounded-md border border-slate-100">
          <table className="w-full text-xs"><tbody>
            {dry.projects.map((p) => <tr key={p.pid} className="border-b border-slate-50 last:border-0"><td className="py-1 px-2 text-slate-700">{p.address} <span className="text-slate-400">{p.pid}</span></td><td className="py-1 px-2 text-right">{p.count} {zh ? "笔" : ""}</td><td className="py-1 px-2 text-right font-semibold">{money(p.total)}</td><td className="py-1 px-2 text-slate-400">{p.checks.join(", ")}</td></tr>)}
          </tbody></table>
        </div>
      )}
      {d.logs?.length > 0 && (
        <div className="mt-3" data-testid="voucher-mail-logs">
          <p className="text-[11px] font-semibold text-slate-500 mb-1">{zh ? "发送记录" : "History"}</p>
          <div className="max-h-36 overflow-auto rounded-md border border-slate-100">
            <table className="w-full text-xs"><tbody>
              {d.logs.map((l) => (
                <tr key={l.id} className="border-b border-slate-50 last:border-0" data-testid={`voucher-mail-log-${l.id}`}>
                  <td className="py-1 px-2 text-slate-500 whitespace-nowrap">{fmtDT(l.at)}</td>
                  <td className="py-1 px-2 font-semibold text-slate-700">{l.period}</td>
                  <td className="py-1 px-2 text-slate-600">{l.count} {zh ? "笔" : ""} · {money(l.total)} · {l.projects} {zh ? "项目" : "proj"}</td>
                  <td className="py-1 px-2 text-slate-500">{l.by}</td>
                  <td className="py-1 px-2">{l.sentOk === (l.recipients || []).length ? <span className="inline-flex items-center gap-0.5 text-emerald-600"><CheckCircle2 className="h-3 w-3" />{l.sentOk}/{(l.recipients || []).length}</span> : <span className="inline-flex items-center gap-0.5 text-red-600"><XCircle className="h-3 w-3" />{l.sentOk}/{(l.recipients || []).length}</span>}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </div>
      )}
      {preview && (
        <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="voucher-mail-preview-dialog">
            <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100">
              <Mail className="h-5 w-5 text-brand-500" />
              <div className="min-w-0">
                <h4 className="font-semibold text-slate-900 text-sm truncate" data-testid="voucher-mail-preview-subject">{preview.subject}</h4>
                <p className="text-[11px] text-slate-500 truncate">{zh ? "发件人" : "From"} {preview.sender || "—"} · {zh ? "收件人" : "To"} <span data-testid="voucher-mail-preview-to">{(preview.recipients || []).join(", ") || (zh ? "（未配置会计邮箱）" : "(none)")}</span></p>
              </div>
              <button onClick={() => setPreview(null)} className="ml-auto p-1.5 rounded-full hover:bg-slate-100" data-testid="voucher-mail-preview-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="px-5 py-2 border-b border-slate-100 flex flex-wrap gap-2 text-xs" data-testid="voucher-mail-preview-attachments">
              <span className="text-slate-500 inline-flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" />{zh ? "附件" : "Attachments"}</span>
              {(preview.attachments || []).map((a) => (
                <span key={a.name} className="inline-flex items-center gap-1 bg-slate-100 rounded-md px-2 py-1 text-slate-700" title={a.note || ""}>
                  <b>{a.name}</b> · {(a.bytes / 1024 / 1024).toFixed(2)} MB{a.entries ? ` · ${a.entries} ${zh ? "个文件" : "files"}` : ""}
                </span>
              ))}
              <span className="text-slate-400">{zh ? `${preview.projects?.length || 0} 个项目 · ${preview.count} 笔 · ${money(preview.total)}` : `${preview.count} batches`}</span>
            </div>
            <iframe title="email-preview" sandbox="" srcDoc={preview.html} className="flex-1 min-h-[420px] w-full bg-slate-50" data-testid="voucher-mail-preview-body" />
            <div className="px-5 py-2.5 border-t border-slate-100 text-[11px] text-slate-400">{zh ? "以上为会计将收到的邮件正文与附件（预览未发送）。确认无误后请点卡片上的「立即发送该月」。" : "Preview only — nothing sent. Use “Send now” on the card to send."}</div>
          </div>
        </div>
      )}
    </div>
  );
};
